import { ReactNode, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { InfoModalTrigger } from "@/components/ui/modal";
import { Progress } from "@/components/ui/progress";
import { VideoMetricRow } from "@/features/reports/components/video-metric-row";
import { VIDEO_METRIC_DEFINITIONS_MODAL_CONFIG } from "@/features/reports/modals/video-metric-definitions-modal";
import { cn } from "@/utils/classes";

/* Prop Types */
interface VideoPillarRowProps {
  label: string;
  score: number | null;
  qualityScores: { label: string; score: number }[];
  infoModalContent: ReactNode;
}

/**
 * VideoPillarRow component
 *
 * Renders a video pillar (Attention / Emotion / Memory) as a collapsible row: its score as a
 * progress bar + percentage, expanding to show its quality scores. A null score shows "-" and can't expand.
 */
export const VideoPillarRow = ({ label, score, qualityScores, infoModalContent }: VideoPillarRowProps) => {
  /* State */
  const [isExpanded, setIsExpanded] = useState(false);

  /* Consts */
  const hasScore = score !== null;
  // Open/close are set explicitly so daisyUI's focus-to-open behaviour doesn't fight the toggle
  const isOpen = hasScore && isExpanded;

  /* Render */
  return (
    <div className={cn("collapse rounded-none bg-base-200", isOpen ? "collapse-open" : "collapse-close")}>
      <div className="collapse-title flex items-center gap-2 px-4 py-3 min-h-0">
        <span className="text-sm font-semibold">{label}</span>
        <InfoModalTrigger modalContent={infoModalContent} config={VIDEO_METRIC_DEFINITIONS_MODAL_CONFIG} iconClassName="size-4 text-base-content/50" />
        <button
          type="button"
          onClick={() => setIsExpanded((expanded) => !expanded)}
          disabled={!hasScore}
          aria-expanded={isOpen}
          aria-label={`${isOpen ? "Collapse" : "Expand"} ${label}`}
          className="flex items-center justify-end gap-2 grow cursor-pointer disabled:cursor-default"
        >
          <Progress value={hasScore ? Math.round(score) : 0} className="w-[69px] h-1.5" />
          <span className="text-sm font-semibold w-10 text-right">{hasScore ? `${Math.round(score)}%` : "-"}</span>
          <ChevronDownIcon className={cn("size-5 transition-transform", { "rotate-180": isOpen, "opacity-30": !hasScore })} />
        </button>
      </div>

      {isOpen && (
        <div className="collapse-content flex flex-col gap-3 px-4">
          {qualityScores.map((qualityScore) => (
            <VideoMetricRow key={qualityScore.label} label={qualityScore.label} score={qualityScore.score} />
          ))}
        </div>
      )}
    </div>
  );
};
